import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud, FileText, RefreshCw, Trash2, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, authUrl, BRANCH_COLORS, BRANCH_TITLES } from "@/lib/api";
import { NatureBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { t, locale } from "@/lib/i18n";

export const DocumentUploader = ({ onChange }) => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const load = () => api.get("/documents").then((r) => { setDocs(r.data); onChange?.(); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!docs.some((d) => d.status === "in_elaborazione")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [docs]);

  const upload = async (files) => {
    for (const f of Array.from(files)) {
      const fd = new FormData(); fd.append("file", f);
      setBusy(true);
      try { await api.post("/documents/upload", fd); toast.success(t("«{f}» caricato: assimilazione in corso", { f: f.name })); }
      catch (e) { toast.error(e.response?.data?.detail || t("Errore su {f}", { f: f.name })); }
    }
    setBusy(false); load();
  };
  const del = (id) => api.delete(`/documents/${id}`).then(load);
  const reprocess = (id) => api.post(`/documents/${id}/reprocess`).then(load);

  return (
    <div className="space-y-4">
      <div data-testid="document-dropzone" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }} onClick={() => inputRef.current.click()}
        className={`surface p-6 border-dashed cursor-pointer text-center transition-colors ${drag ? "border-primary bg-primary/10" : "hover:border-primary/60"}`}>
        <UploadCloud size={26} className="mx-auto text-primary mb-2" />
        <div className="font-heading font-semibold">{t("Carica dispense ed Excel reali")}</div>
        <p className="text-xs text-muted-foreground mt-1">{t("PDF, XLSX, DOCX, TXT fino a 50 MB. Il Direttore di Metodo (GPT 5.4) legge il file, lo classifica nell'albero, collega i nodi e aggiorna la maturità dei rami.")}</p>
        <input ref={inputRef} data-testid="document-file-input" type="file" multiple accept=".pdf,.xlsx,.xlsm,.docx,.txt,.csv,.md" className="hidden" onChange={(e) => upload(e.target.files)} />
        {busy && <div className="text-xs text-primary mt-2 animate-pulse">{t("Caricamento…")}</div>}
      </div>
      {docs.length > 0 && (
        <div className="space-y-2">
          <div className="eyebrow">Documenti caricati ({docs.length})</div>
          {docs.map((d) => (
            <article key={d.id} data-testid={`document-card-${d.id}`} className="surface p-4" style={{ borderLeft: `3px solid ${d.primary_branch ? BRANCH_COLORS[d.primary_branch] : "#443E38"}` }}>
              <div className="flex items-start gap-3">
                <FileText size={18} className="text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm">{d.filename}</span>
                    <span data-testid={`document-status-${d.id}`} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${d.status === "assimilato" ? "text-emerald-300 border-emerald-700" : "text-amber-300 border-amber-700 animate-pulse"}`}>{t(d.status === "assimilato" ? "assimilato" : "in elaborazione")}</span>
                    {d.ai_used && <span className="text-[10px] text-primary flex items-center gap-1"><Sparkles size={10} /> AI</span>}
                    {d.nature && <NatureBadge nature={d.nature} />}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{(d.chars / 1000).toFixed(1)}k caratteri · {new Date(d.created_at).toLocaleDateString(locale())}</div>
                  {d.summary && <p className="text-xs text-foreground/85 mt-2">{d.summary}</p>}
                  {d.status === "assimilato" && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {d.primary_branch && <Link to={`/?node=${d.primary_branch}`} className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${BRANCH_COLORS[d.primary_branch]}22`, color: BRANCH_COLORS[d.primary_branch] }}>{BRANCH_TITLES[d.primary_branch]}</Link>}
                      {(d.nodes || []).map((n) => <Link key={n} to={`/?node=${n}`} className="text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:text-primary">↗ {n}</Link>)}
                    </div>
                  )}
                  {d.new_concepts?.length > 0 && <div className="text-[11px] text-amber-300/90 mt-2">{t("Nuovi concetti per l'albero:")} {d.new_concepts.join(", ")}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <a data-testid={`document-download-${d.id}`} href={authUrl(`/documents/${d.id}/download`)} className="p-1.5 text-muted-foreground hover:text-foreground" title={t("Scarica")}><Download size={14} /></a>
                  <button data-testid={`document-reprocess-${d.id}`} onClick={() => reprocess(d.id)} className="p-1.5 text-muted-foreground hover:text-primary" title={t("Rielabora")}><RefreshCw size={14} /></button>
                  <button data-testid={`document-delete-${d.id}`} onClick={() => del(d.id)} className="p-1.5 text-muted-foreground hover:text-destructive" title={t("Elimina")}><Trash2 size={14} /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
